import { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  GuideLinks as GuideLinksData,
  GuideLinksSection,
} from '@silvertours/back-domain-page';
import { Button } from '@silvertours/front-shared';

import fallbackImage from './fallback.webp';
import {
  Section,
  Header,
  Headline,
  FeaturedGrid,
  FeaturedCard,
  FeaturedCardWide,
  CardImageWrapper,
  CardImg,
  CardBody,
  CardTitle,
  CardIntro,
  ReadMoreLink,
  AdaptiveGrid,
  ButtonWrapper,
  CardBottomContent,
  GuideSectionContainer,
  HiddenItemsWrapper,
} from './GuideLinks.styles';

type Props = {
  links: GuideLinksData;
};

const ArrowRight = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M6 3L11 8L6 13"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type GuideItem = GuideLinksSection['items'][0];

const GuideSection = ({
  section,
  readArticleLabel,
  showMoreLabel,
  showLessLabel,
}: {
  section: GuideLinksSection;
  readArticleLabel: string;
  showMoreLabel: string;
  showLessLabel: string;
}) => {
  const [showAll, setShowAll] = useState(false);
  const { visibleCount, visibleBoxType, hiddenBoxType } = section.display;

  const visibleItems = section.items.slice(0, visibleCount);
  const hiddenItems = section.items.slice(visibleCount);
  const hasMore = !showAll && hiddenItems.length > 0;
  const hasHidden = showAll && hiddenItems.length > 0;

  const renderImageCard = (
    item: GuideItem,
    CardComponent: typeof FeaturedCard,
  ) => (
    <CardComponent key={item.url} href={item.url}>
      <CardImageWrapper>
        {item.image ? (
          <CardImg
            src={item.image.src}
            alt={item.image.alt}
            fill
            sizes="(max-width: 768px) 100vw, 50vw"
          />
        ) : (
          <CardImg
            src={fallbackImage}
            alt=""
            fill
            sizes="(max-width: 768px) 100vw, 50vw"
          />
        )}
      </CardImageWrapper>
      <CardBody>
        <CardTitle>{item.title}</CardTitle>
        <CardBottomContent>
          {item.textIntro && <CardIntro>{item.textIntro}</CardIntro>}
          <ReadMoreLink>
            {readArticleLabel}
            <ArrowRight />
          </ReadMoreLink>
        </CardBottomContent>
      </CardBody>
    </CardComponent>
  );

  const renderHeadlineCard = (item: GuideItem) => (
    <FeaturedCard key={item.url} href={item.url}>
      <CardBody>
        <CardTitle>{item.title}</CardTitle>
        <ReadMoreLink>
          {readArticleLabel}
          <ArrowRight />
        </ReadMoreLink>
      </CardBody>
    </FeaturedCard>
  );

  const renderGroup = (
    items: GuideItem[],
    isImageType: boolean,
    useFeaturedLayout: boolean,
  ) => {
    if (useFeaturedLayout && items.length >= 5) {
      return (
        <FeaturedGrid>
          {renderImageCard(items[0], FeaturedCardWide)}
          {items.slice(1, 5).map(item => renderImageCard(item, FeaturedCard))}
        </FeaturedGrid>
      );
    }

    const isSingle = items.length === 1;
    const cols = isSingle ? 2 : Math.min(items.length, 4);
    return (
      <AdaptiveGrid columns={cols} singleItem={isSingle}>
        {items.map(item =>
          isImageType
            ? renderImageCard(item, FeaturedCard)
            : renderHeadlineCard(item),
        )}
      </AdaptiveGrid>
    );
  };

  return (
    <GuideSectionContainer>
      <Header>
        <Headline>{section.headLine}</Headline>
      </Header>

      {visibleItems.length > 0 &&
        renderGroup(
          visibleItems,
          visibleBoxType === 'image',
          visibleBoxType === 'image' && visibleCount === 5,
        )}

      {hiddenItems.length > 0 && (
        <HiddenItemsWrapper visible={showAll} aria-hidden={!showAll}>
          {renderGroup(hiddenItems, hiddenBoxType === 'image', false)}
        </HiddenItemsWrapper>
      )}

      {hiddenItems.length > 0 && (
        <>
          {hasMore && (
            <ButtonWrapper>
              <Button variant="secondary" onClick={() => setShowAll(true)}>
                {showMoreLabel}
              </Button>
            </ButtonWrapper>
          )}
          {hasHidden && (
            <ButtonWrapper>
              <Button variant="secondary" onClick={() => setShowAll(false)}>
                {showLessLabel}
              </Button>
            </ButtonWrapper>
          )}
        </>
      )}
    </GuideSectionContainer>
  );
};

const GuideLinks = ({ links }: Props) => {
  const t = useTranslations('features.guideLinks');

  if (!links.length) return null;

  return (
    <Section>
      {links.map((section, index) => (
        <GuideSection
          key={`${index}-${section.headLine}`}
          section={section}
          readArticleLabel={t('readArticle')}
          showMoreLabel={t('showMore')}
          showLessLabel={t('showLess')}
        />
      ))}
    </Section>
  );
};

export { GuideLinks };
